import { jsonAvecCitationsAffichees } from './citations';
// Mise en forme française des montants, pourcentages, dates, et libellés des valeurs du format pivot.
import type { Gravite, Operation, StatutPartage, PoidsRaison, TypeLien, TypeObjet, VueAnssi } from '../api/types';

const ESPACE = ' '; // espace fine insécable (séparateur des milliers et avant €)

function nombre(v: number, decimales: number): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: decimales, minimumFractionDigits: decimales })
    .format(v)
    .replace(/ | /g, ESPACE);
}

/** « 3,7 M€ », « 670 k€ », « 950 € ». */
export function eurosCourt(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e6) return `${nombre(v / 1e6, a >= 1e8 ? 0 : 1)}${ESPACE}M€`;
  if (a >= 1e3) return `${nombre(v / 1e3, 0)}${ESPACE}k€`;
  return `${nombre(v, 0)}${ESPACE}€`;
}

/** « 3 670 192 € » (arrondi à l'euro). */
export function eurosLong(v: number): string {
  return `${nombre(Math.round(v), 0)}${ESPACE}€`;
}

/** « 3,7 à 7,5 M€ » : fourchette du moteur (valeur basse, valeur haute). */
export function fourchette(bas: number, haut: number): string {
  if (Math.round(bas) === Math.round(haut)) return eurosCourt(haut);
  const ub = eurosCourt(bas).split(ESPACE).pop();
  const uh = eurosCourt(haut).split(ESPACE).pop();
  if (ub === uh) return `${eurosCourt(bas).replace(`${ESPACE}${ub}`, '')} à ${eurosCourt(haut)}`;
  return `${eurosCourt(bas)} à ${eurosCourt(haut)}`;
}

/** Nombre décimal à la française (« 0,03 »). */
export function decimal(v: number): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 }).format(v).replace(/\u00a0|\u202f/g, ESPACE);
}

export function pourcent(v: number): string {
  return `${nombre(v * 100, 0)}${ESPACE}%`;
}

export function dateFr(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const avecHeure = iso.length > 10;
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    ...(avecHeure ? { timeStyle: 'short' } : {}),
    timeZone: avecHeure ? undefined : 'UTC',
  }).format(d);
}

export const LIBELLE_TYPE: Record<TypeObjet, string> = {
  processus: 'Processus',
  acteur: 'Acteur',
  application: 'Application',
  agent_ia: 'Agent d’IA',
  interface: 'Interface',
  donnee: 'Donnée',
  machine: 'Machine',
  zone_reseau: 'Zone réseau',
  site: 'Site',
  compte_privilegie: 'Compte à privilèges',
  fournisseur: 'Fournisseur',
  contrat: 'Contrat',
  protection: 'Protection',
  sauvegarde: 'Sauvegarde',
  constat: 'Constat',
  exigence: 'Exigence',
  contrainte: 'Contrainte',
  travaux: 'Travaux',
  decision: 'Décision',
  signal: 'Signal',
};

export const LIBELLE_LIEN: Record<TypeLien, string> = {
  lit: 'lit',
  ecrit: 'écrit',
  depend_de: 'dépend de',
  necessite: 'nécessite',
  situe_dans: 'situé dans',
  implante_sur: 'implanté sur',
  fourni_par: 'fourni par',
  proprietaire_de: 'propriétaire de',
  passage_reseau: 'passage réseau vers',
  flux: 'flux vers',
  contient: 'contient',
  administre: 'administre',
  protege: 'protège',
  sauvegarde: 'sauvegarde',
  concerne: 'concerne',
  satisfait: 'satisfait',
  contraint: 'contraint',
  porte_sur: 'porte sur',
  decide: 'décide',
  lie_par: 'lié par',
};

export const LIBELLE_GRAVITE: Record<Gravite, string> = {
  information: 'Information',
  faible: 'Faible',
  moyenne: 'Moyenne',
  elevee: 'Élevée',
  critique: 'Critique',
};

/**
 * Statut d'une mesure NIS2 tel qu'une personne le lit : « Tenue », « Partielle » ou « En écart ». Une mesure n'est pas
 * « conforme » (cela se lirait comme une certification, docs/REGLE-EDITORIALE.md). Les clés sont les valeurs
 * techniques du moteur (`conforme`, `partiel`, `ecart`), qui ne changent pas ; mêmes mots que la console.
 */
export const LIBELLE_STATUT_NIS2: Record<string, string> = {
  conforme: 'Tenue',
  partiel: 'Partielle',
  ecart: 'En écart',
};

/**
 * Nature d'un constat telle qu'une personne la lit, avec les mots de la console (`NATURE_LIBELLE`). Les clés sont les
 * valeurs techniques de la carte. Un constat de nature `conformite` vient d'une mesure NIS2 qui n'est pas tenue : il se
 * lit « Mesure NIS2 en écart ou partielle », sans le mot « conformité ».
 */
export const LIBELLE_NATURE_CONSTAT: Record<string, string> = {
  vulnerabilite: 'Vulnérabilité',
  conformite: 'Mesure NIS2 en écart ou partielle',
  configuration: 'Configuration',
  contradiction: 'Contradiction entre pièces',
};

export const LIBELLE_STATUT_FAIT: Record<string, string> = {
  propose: 'Proposé par Ekera',
  confirme: 'Confirmé',
  corrige: 'Corrigé par le DSI',
  rejete: 'Rejeté',
};

export const LIBELLE_OPERATION: Record<Operation | 'annuler' | 'charger', string> = {
  modifier: 'Corriger',
  valider: 'Valider',
  retirer: 'Retirer',
  creer: 'Créer',
  lier: 'Lier',
  // annulation d'une écriture (API, rôle administration) : visible au journal, pas encore proposée par l'écran
  annuler: 'Annuler',
  // chargement d'une carte par l'opérateur (D4) : le type généré ne le liste pas encore
  charger: 'Charger',
};

export const LIBELLE_PARTAGE: Record<StatutPartage, string> = {
  partageable: 'Partageable',
  generalisable: 'Partageable une fois généralisé',
  prive: 'Privé',
};

export const LIBELLE_POIDS: Record<PoidsRaison, string> = {
  decisive: 'Décisive',
  importante: 'Importante',
  secondaire: 'Secondaire',
};

export const VUES_ANSSI: { id: VueAnssi; libelle: string; description: string }[] = [
  { id: 'ecosysteme', libelle: 'Écosystème', description: 'Fournisseurs et partenaires' },
  { id: 'metier', libelle: 'Métier', description: 'Processus et acteurs' },
  { id: 'applications', libelle: 'Applications', description: 'Applications et leurs dépendances' },
  { id: 'administration', libelle: 'Administration', description: 'Périmètres et privilèges des administrateurs' },
  { id: 'infrastructures_logiques', libelle: 'Infrastructures logiques', description: 'Zones et passages réseau' },
  { id: 'infrastructures_physiques', libelle: 'Infrastructures physiques', description: 'Équipements et sites' },
];

export const POSTES_PERTE: { cle: string; libelle: string; compte: boolean }[] = [
  { cle: 'marge_perdue', libelle: 'Marge perdue pendant l’arrêt', compte: true },
  { cle: 'cout_rattrapage', libelle: 'Coût du rattrapage', compte: true },
  { cle: 'penalites', libelle: 'Pénalités contractuelles', compte: true },
  { cle: 'reconstitution_donnees', libelle: 'Reconstitution des données', compte: true },
  { cle: 'reponse_incident', libelle: 'Réponse à l’incident', compte: true },
  { cle: 'financement', libelle: 'Coût de financement de la trésorerie différée', compte: true },
  { cle: 'tresorerie_differee', libelle: 'Trésorerie différée (encaissements retardés, non comptée)', compte: false },
];

/** « composant:erp » → « erp » (repli quand l'objet n'a pas de nom). */
export function idCourt(id: string): string {
  return id.includes(':') ? id.slice(id.indexOf(':') + 1) : id;
}

export function valeurLisible(v: unknown): string {
  if (v === null) return 'inconnu';
  if (v === undefined) return 'non renseigné';
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (typeof v === 'number') return nombre(v, Number.isInteger(v) ? 0 : 2);
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v && 'valeur' in v && typeof (v as { valeur: unknown }).valeur === 'number') {
    const m = v as { valeur: number; periode?: string };
    const per = m.periode && m.periode !== 'unique' ? ` par ${m.periode}` : '';
    return `${eurosLong(m.valeur)}${per}`;
  }
  return jsonAvecCitationsAffichees(v);
}

/**
 * Le nom d'une personne tel que l'API le donne, jamais un identifiant technique : un rapport ou une réponse enregistrés
 * en développement ont pu garder « developpement:administration » (sujet du jeton). Tout nom sans espace et avec « : »
 * (`developpement:administration`, `humain:3fa9…`, `xxx:yyy`) a la forme d'un identifiant, ou est forgé pour y ressembler :
 * il est masqué (`null`, l'écran dit « une personne »), jamais lu « l'administrateur ».
 */
export function nomDePersonne(nom?: string | null): string | null {
  if (!nom || !nom.trim()) return null;
  return !/\s/.test(nom) && nom.includes(':') ? null : nom;
}

// ---------------------------------------------------------------- notes de provenance

/** Champs de l'ontologie que les notes de provenance nomment tels quels (« owner nul, managed faux »). */
const LIBELLE_CHAMP: Record<string, string> = {
  owner: 'responsable',
  managed: 'géré par l’entreprise',
  supplier: 'prestataire',
  exposed: 'exposé à Internet',
  mfa: 'double authentification',
  sso: 'authentification unique',
  offline: 'copie hors ligne',
  domain: 'membre du domaine',
  endOfSupport: 'fin de support',
  vulnerable: 'vulnérabilité connue',
  redundant: 'redondance',
  phishing: 'hameçonnage',
  needs: 'dépendances',
  asOf: 'date de référence',
  architect: 'architecte',
  summary: 'résumé',
  yearlyAudit: 'audit annuel',
  encryption: 'chiffrement',
  dayValue: 'valeur d’une journée',
  gestureLabel: 'libellé du geste',
  gestureDetail: 'détail du geste',
  cost: 'coût',
};
const LIBELLE_VALEUR: Record<string, string> = { nul: 'aucun', vrai: 'oui', faux: 'non' };

/**
 * Une note de provenance écrite avec les clés techniques de l'ontologie, lisible en français :
 * « (owner nul, managed faux) » devient « (responsable : aucun, géré par l’entreprise : non) ». Un mot qui n'est pas
 * une clé connue reste tel quel ; le texte d'origine reste consultable dans « Détails techniques ».
 */
export function noteLisible(note: string): string {
  return note
    .replace(/\b([A-Za-z]+) (nul|vrai|faux)\b/g, (m, cle: string, v: string) =>
      LIBELLE_CHAMP[cle] ? `${LIBELLE_CHAMP[cle]} : ${LIBELLE_VALEUR[v]}` : m,
    )
    .replace(/\b[A-Za-z]+\b/g, (mot) => LIBELLE_CHAMP[mot] ?? mot);
}
